import { Feedback, Input, Field } from '@availity/form';
import { Formik } from 'formik';
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Label } from 'reactstrap';
import { FileSelector } from '../../../../components/FileSelector/FileSelector';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import {
    ActionDocumentType,
    EngagementDocumentTypeOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import {
    EngagementDocumentSource,
    EngagementDocumentSourceOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { useStateSelector } from '../../../../store/selectors';
import * as yup from 'yup';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import './SellerEngagementDocumentForm.scss';
import { FileSelectorService } from '../../../../components/FileSelector/FileSelector.service';
import Helpers from '../../../../utils/helper';
import createNotification from '../../../../utils/createNotification';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import {
    DocumentDetailsContentType,
    DocumentDetailsContentTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/document-details-content-type';

interface SellerEngagementDocumentFormProps {
    onSubmit(action: TimelineAction, documentDetails?: DocumentDetails): void;
    disabled: boolean;
    downloadFile(): void;
    documentDetails?: DocumentDetails;
    documentsSections: DocumentSection[];
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    setIsFileUploading(isUploading: boolean): void;
    setIsDocumentsHistoryPopupVisible(isVisible: boolean): void;
    setDocumentSectionList(sections: DocumentSection[]): void;
    documentDetailsList: DocumentDetails[];
}
export const SellerEngagementDocumentForm = forwardRef(
    (props: SellerEngagementDocumentFormProps, ref) => {
        const formRef = useRef<any>();
        const axios = useStateSelector((state) => state.core.axios);
        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );
        const [selectedFile, setSelectedFile] = useState<File | null>(null);
        const [oldDocumentId, setOldDocumentId] = useState<string>(null);
        const [isFileUpdated, setIsFileUpdated] = useState(false);

        const getDefaultDocumentDetails = () => {
            return {
                id: Helpers.generateGUID(),
                name: '',
                status: DocumentDetailsStatus.RequiresAction,
                counterParty: TimelineParty.Seller,
                sectionId: '',
                type: -1 as ActionDocumentType,
                contentType: DocumentDetailsContentType.General,
                isUnregisteredFile: true,
                source: -1 as EngagementDocumentSource,
                templateId: 0,
                fileName: '',
            };
        };

        const [documentDetails, setDocumentDetails] = useState(
            props.documentDetails ?? getDefaultDocumentDetails()
        );

        const [fileSelectorService] = useState(new FileSelectorService(axios));

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const uploadFile = async () => {
            props.setIsFileUploading(true);
            const fileId = await fileSelectorService.uploadFile(selectedFile);
            props.setIsFileUploading(false);
            return {
                ...documentDetails,
                temporaryFileId: fileId,
                fileName: selectedFile?.name,
            };
        };

        const changeFileHandler = (file: File) => {
            if (file) {
                setIsFileUpdated(true);
                setSelectedFile(file);
                formRef.current.setFieldValue('fileName', file.name, true);
                formRef.current.setFieldValue('sourceFile', file.name, true);

                const newDocument: DocumentDetails = {
                    ...documentDetails,
                    id: Helpers.generateGUID(),
                    name: file.name,
                    isUnregisteredFile: true,
                    localFile: file,
                    source: EngagementDocumentSource.AdminUpload,
                };

                setOldDocumentId(documentDetails.id);
                setDocumentDetails(newDocument);
            }
        };

        return (
            <div className="seller-engagement-document-form">
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        documentSource: documentDetails.source,
                        documentSection: documentDetails.sectionId,
                        documentName: documentDetails.name ?? '',
                        actionType: documentDetails.type,
                        contentType: documentDetails.contentType,
                        status: documentDetails.status,
                        documentTemplate: documentDetails.templateId ?? -1,
                        fileName: documentDetails.fileName ?? '',
                        sourceFile: documentDetails.fileName
                            ? documentDetails.fileName.replace(
                                  /(_\d+)(\.[^.]+)$/,
                                  '$2'
                              )
                            : '',
                    }}
                    validationSchema={yup.object().shape({
                        actionType: yup
                            .number()
                            .min(0, 'Action type is required')
                            .uniqueGroupValidation(
                                props.timelineEvents ?? [],
                                props.currentEvent
                            ),
                        documentSource: yup
                            .number()
                            .min(0, 'Document source is required'),
                        documentSection: yup
                            .string()
                            .required('Document section is required')
                            .notOneOf(['-1'], 'Document section is required'),
                        documentTemplate: yup.number().when('documentSource', {
                            is: (val: EngagementDocumentSource) =>
                                Number(val) ===
                                EngagementDocumentSource.Template,
                            then: () =>
                                yup.number().min(1, 'Template is required'),
                        }),
                        contentType: yup.number().when('actionType', {
                            is: (val: ActionDocumentType) =>
                                Number(val) ===
                                ActionDocumentType.DocumentWithFeedback,
                            then: () => yup.number().min(0, 'Type is required'),
                        }),
                        documentName: yup
                            .string()
                            .trim()
                            .required('Document name is required'),
                    })}
                    onSubmit={async (values) => {
                        let docDetails = isFileUpdated
                            ? await uploadFile()
                            : documentDetails;
                        docDetails = {
                            ...docDetails,
                            name: values.documentName.trim(),
                            sectionId: values.documentSection,
                            type: values.actionType,
                            contentType: values.contentType,
                            status: values.status,
                            counterParty: TimelineParty.Seller,
                            source: values.documentSource,
                            templateId: values.documentTemplate,
                        };
                        if (
                            documentDetails.type ==
                                ActionDocumentType.DocumentWithFeedback &&
                            values.actionType !=
                                ActionDocumentType.DocumentWithFeedback
                        ) {
                            const indexOfEventWithRefToThisAction =
                                props.timelineEvents.findIndex(
                                    (e) =>
                                        e.linkedAction?.actionReferenceId ===
                                        props.linkedAction?.id
                                );
                            if (indexOfEventWithRefToThisAction > -1) {
                                createNotification(
                                    `You can't change the Engagement document type because it is used in the event with index ${
                                        indexOfEventWithRefToThisAction + 1
                                    }.`,
                                    'error'
                                );
                                return;
                            }
                        }
                        props.onSubmit(
                            props.linkedAction
                                ? {
                                      ...props.linkedAction,
                                      oldReferenceItemIds: [
                                          ...(props.linkedAction
                                              .oldReferenceItemIds ?? []),
                                          oldDocumentId,
                                      ],
                                      actionType: Number(
                                          TimelineActionType.SellerEngagementDocument
                                      ),
                                      referenceItemId: docDetails.id,
                                  }
                                : {
                                      ...defaultTimelineAction,
                                      actionType: Number(
                                          TimelineActionType.SellerEngagementDocument
                                      ),
                                      referenceItemId: docDetails.id,
                                  },
                            docDetails
                        );
                    }}>
                    {({ values, setFieldValue }) => {
                        return (
                            <div className="action-expanded-sub-form">
                                <Label for="actionType" className="mt-0">
                                    Action Type
                                </Label>
                                <Input
                                    type="select"
                                    name="actionType"
                                    disabled={props.disabled}
                                    onChange={(e) => {
                                        const newActionType = Number(
                                            e.target.value
                                        );
                                        const currentActionType = Number(
                                            values.actionType
                                        );
                                        const actionStatus =
                                            newActionType ===
                                                ActionDocumentType.Downloadable ||
                                            newActionType ===
                                                ActionDocumentType.Readonly
                                                ? DocumentDetailsStatus.Viewable
                                                : DocumentDetailsStatus.RequiresAction;

                                        setFieldValue(
                                            'status',
                                            actionStatus,
                                            true
                                        );
                                        if (
                                            !EngagementDocumentTypeOptionsMapping.find(
                                                (o) => o.value === newActionType
                                            )
                                                ?.fileExtensions?.split(',')
                                                .some((ext) =>
                                                    values.fileName.endsWith(
                                                        ext.trim()
                                                    )
                                                )
                                        ) {
                                            setFieldValue('fileName', '', true);
                                            setSelectedFile(null);
                                        }
                                        if (
                                            EngagementDocumentSourceOptionsMapping.some(
                                                (o) =>
                                                    o.documentTypes.includes(
                                                        newActionType
                                                    ) &&
                                                    !o.documentTypes.includes(
                                                        currentActionType
                                                    )
                                            )
                                        ) {
                                            setFieldValue(
                                                'documentSource',
                                                -1,
                                                true
                                            );
                                            setFieldValue(
                                                'documentTemplate',
                                                0,
                                                true
                                            );
                                            setFieldValue('fileName', '', true);
                                            setSelectedFile(null);
                                            setDocumentDetails(
                                                getDefaultDocumentDetails()
                                            );
                                        }
                                    }}>
                                    <option value={-1}>Select</option>
                                    {EngagementDocumentTypeOptionsMapping.map(
                                        (option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}>
                                                {option.label}
                                            </option>
                                        )
                                    )}
                                </Input>
                                <Feedback name="actionType" />
                                {(() => {
                                    switch (Number(values.actionType)) {
                                        case ActionDocumentType.DocumentWithFeedback:
                                            return (
                                                <>
                                                    <Label for="contentType">
                                                        Document Type
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="contentType"
                                                        disabled={
                                                            props.disabled
                                                        }>
                                                        {DocumentDetailsContentTypeOptions.map(
                                                            (option) => (
                                                                <option
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="contentType" />
                                                </>
                                            );

                                        default:
                                            return null;
                                    }
                                })()}

                                <Label for="documentSource">
                                    Document Source
                                </Label>
                                <Input
                                    type="select"
                                    name="documentSource"
                                    disabled={props.disabled}>
                                    <option value={-1}>Select</option>
                                    {EngagementDocumentSourceOptionsMapping.filter(
                                        (o) =>
                                            o.documentTypes.includes(
                                                Number(values.actionType)
                                            )
                                    ).map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Input>
                                <Feedback name="documentSource" />

                                <MarketplaceDocumentSectionField
                                    documentsSections={props.documentsSections}
                                    disabled={props.disabled}
                                    setDocumentSectionList={
                                        props.setDocumentSectionList
                                    }
                                    documentDetailsList={
                                        props.documentDetailsList
                                    }
                                    type="sellers"></MarketplaceDocumentSectionField>

                                {(() => {
                                    switch (Number(values.documentSource)) {
                                        case EngagementDocumentSource.Template:
                                            return (
                                                <>
                                                    <Label for="documentTemplate">
                                                        Document Template
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name="documentTemplate"
                                                        disabled={
                                                            props.disabled
                                                        }>
                                                        <option value={0}>
                                                            Select
                                                        </option>
                                                        {documentTemplates.map(
                                                            (option) => (
                                                                <option
                                                                    key={`template-${option.id}`}
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.name
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                    <Feedback name="documentTemplate" />
                                                    {documentDetails.status ===
                                                    DocumentDetailsStatus.Completed ? (
                                                        <label
                                                            className="download-link-button"
                                                            onClick={
                                                                props.downloadFile
                                                            }>
                                                            Download Signed
                                                            Document
                                                        </label>
                                                    ) : (
                                                        []
                                                    )}
                                                </>
                                            );
                                        case EngagementDocumentSource.AdminUpload:
                                            return (
                                                <>
                                                    <FileSelector
                                                        extension={
                                                            EngagementDocumentTypeOptionsMapping.find(
                                                                (o) =>
                                                                    Number(
                                                                        values.actionType
                                                                    ) ===
                                                                    o.value
                                                            ).fileExtensions
                                                        }
                                                        disabled={
                                                            props.disabled
                                                        }
                                                        file={selectedFile}
                                                        setFile={(
                                                            file: File
                                                        ) => {
                                                            changeFileHandler(
                                                                file
                                                            );
                                                        }}
                                                        fileName={
                                                            values.fileName
                                                        }
                                                        downloadFile={
                                                            props.downloadFile
                                                        }
                                                        setIsDocumentsHistoryPopupVisible={
                                                            props.setIsDocumentsHistoryPopupVisible
                                                        }
                                                        oldDocumentDetailsList={
                                                            props.linkedAction
                                                                ?.oldReferenceItemIds
                                                        }
                                                    />
                                                    <Feedback name="fileName" />
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                })()}

                                <Field
                                    className="field"
                                    name="documentName"
                                    label="Document Name"
                                    disabled={props.disabled}
                                />
                            </div>
                        );
                    }}
                </Formik>
            </div>
        );
    }
);
